'use client';

import { faLoader } from '@fortawesome/pro-light-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, InputField } from '@pickleballinc/react-ui';
import _debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';

import { IsManagerDetails } from '@/legacy/utils/tournaments.types';
import { PlayerRow, TourneyPlayer } from '@/tournament/components/players/PlayerRow';

interface PlayerListParams {
	slug: string;
	isManagerDetails: IsManagerDetails;
}

export const PlayerList = ({ slug, isManagerDetails }: PlayerListParams) => {
	const [playersData, setPlayersData] = useState<TourneyPlayer[]>();

	const [searchTerm, setSearchTerm] = useState<string>('');
	const [needAPartnerOnly, setNeedAPartnerOnly] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const getTournamentPlayers = async () => {
		setIsLoading(true);

		const response = await fetch(`/tournaments/api/tourneyPlayers?slug=${slug}&searchTerm=${searchTerm}`);

		const playersRes = (await response.json()) as TourneyPlayer[];
		setPlayersData(playersRes);
		setIsLoading(false);
	};

	const debouncedSearch = useRef(
		_debounce(async (value: string) => {
			setSearchTerm(value);
		}, 75)
	).current;

	async function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		debouncedSearch(e.target.value);
	}

	useEffect(() => {
		getTournamentPlayers();
	}, []);

	return (
		<>
			{isLoading ? (
				<div className="flex h-48 flex-col items-center justify-center gap-4">
					<FontAwesomeIcon className="text-4xl font-extralight" icon={faLoader} spin />
					<div>Loading players...</div>
				</div>
			) : (
				<div className="flex flex-col gap-4">
					<div>
						<Checkbox
							checked={needAPartnerOnly}
							onChange={() => setNeedAPartnerOnly(!needAPartnerOnly)}
							className="checked:border-blue-400 checked:bg-blue-100 hover:border-blue-400 hover:bg-blue-100 focus:border-blue-400 focus:bg-blue-100 focus:!shadow-blue-100 active:!border-blue-400 active:!bg-blue-100 active:!shadow-blue-100"
							Text={'Show only players needing a partner'}
							name="Need a Partner Checkbox"
							size="sm"
						/>
					</div>
					<div className="flex flex-row items-center gap-2">
						<div className="w-full">
							<InputField
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										getTournamentPlayers();
									}
								}}
								placeholder="Search for a Player"
								className=" !border-gray-300 !shadow-blue-100"
								PrefixIcon={() => <FontAwesomeIcon icon={faSearch} />}
								onChange={handleChange}
							/>
						</div>
						<Button
							className="bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-100"
							onClick={() => getTournamentPlayers()}
							variant="secondary"
						>
							Search
						</Button>
					</div>
					{playersData?.map((player, i) => (
						<PlayerRow
							needAPartnerOnly={needAPartnerOnly}
							key={i}
							data={player}
							tourneySlug={slug}
							isManager={isManagerDetails.IsManager}
						/>
					))}
				</div>
			)}
		</>
	);
};
